import { useEffect, useState } from 'react';
import { apiClient, submitEmotionVote, submitFeedback, submitGuestFeedback, submitGuestEmotionVote } from '../api/client';
import { getOrCreateGuestIdentifier } from '../utils/guestIdentifier';
import InlineFeedbackForm from './InlineFeedbackForm';
import { formatAppError } from '../utils/errorMessage';

type EmotionType = 
  'admiration' | 'amusement' | 'anger' | 'annoyance' | 'approval' | 'caring' | 'confusion' | 
  'curiosity' | 'desire' | 'disappointment' | 'disapproval' | 'disgust' | 'embarrassment' | 
  'excitement' | 'fear' | 'gratitude' | 'grief' | 'joy' | 'love' | 'nervousness' | 'optimism' | 
  'pride' | 'realization' | 'relief' | 'remorse' | 'sadness' | 'surprise' | 'neutral';

const EMOTICONS: Record<EmotionType, string> = {
  'admiration': '👏', 'amusement': '😄', 'anger': '😠', 'annoyance': '😒',
  'approval': '👍', 'caring': '🤗', 'confusion': '😕', 'curiosity': '🤔',
  'desire': '😍', 'disappointment': '😞', 'disapproval': '👎', 'disgust': '🤮',
  'embarrassment': '😳', 'excitement': '🤩', 'fear': '😨', 'gratitude': '🙏',
  'grief': '😭', 'joy': '😃', 'love': '❤️', 'nervousness': '😬', 'optimism': '🌞',
  'pride': '🏅', 'realization': '💡', 'relief': '😌', 'remorse': '😔',
  'sadness': '😢', 'surprise': '😲', 'neutral': '😐'
};

interface EmotionDetectorProps {
  isAuthenticated: boolean;
  currentPlan: string | null;
}

interface EmotionResult {
  session_id: string;
  detected_emotions: string[];
  confidence_scores: Record<string, number>;
  sarcasm_detected: boolean;
  sarcasm_score?: number | null;
  detected_language: string;
  recommendation?: string | null;
}

interface QuotaInfo {
  plan?: string;
  used?: number;
  limit?: number;
  remaining?: number;
  resetAt?: string;
}


export default function EmotionDetector({ isAuthenticated, currentPlan }: EmotionDetectorProps) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<EmotionResult | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [voted, setVoted] = useState<{ [emotion: string]: boolean | null }>({});
  const [voteStatus, setVoteStatus] = useState<{ [emotion: string]: string }>({});
  const [feedbackId, setFeedbackId] = useState<number | null>(null);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  
  // Inline feedback state - moved here to persist between show/hide
  const [showInlineFeedback, setShowInlineFeedback] = useState(false);
  const [selectedEmotions, setSelectedEmotions] = useState<string[]>([]);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [sarcasmFeedback, setSarcasmFeedback] = useState<boolean | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const getPlanLabel = (planName: string | null): string => {
    if (!planName) return 'Account';
    if (planName === 'starter') return 'Starter Plan';
    if (planName === 'pro') return 'Pro Plan';
    if (planName === 'scale') return 'Scale Plan';
    if (planName === 'enterprise') return 'Enterprise Plan';
    return 'Free Plan';
  };

  const normalizeResetDate = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
  };

  const readQuotaFromHeaders = (headers: any): QuotaInfo | null => {
    const limitRaw = headers?.['x-ratelimit-limit'];
    const remainingRaw = headers?.['x-ratelimit-remaining'];
    const resetRaw = headers?.['x-ratelimit-reset'];

    if (limitRaw === undefined && remainingRaw === undefined && !resetRaw) {
      return null;
    }

    const limit = Number(limitRaw);
    const remaining = Number(remainingRaw);
    const normalizedLimit = Number.isFinite(limit) ? limit : undefined;
    const normalizedRemaining = Number.isFinite(remaining) ? remaining : undefined;

    return {
      limit: normalizedLimit,
      remaining: normalizedRemaining,
      used:
        normalizedLimit !== undefined && normalizedRemaining !== undefined
          ? Math.max(normalizedLimit - normalizedRemaining, 0)
          : undefined,
      resetAt: normalizeResetDate(typeof resetRaw === 'string' ? resetRaw : undefined),
    };
  };

  const createFeedbackRecord = async (
    useAuthenticatedFlow: boolean,
    detectionResult: EmotionResult,
    language: string
  ): Promise<number | null> => {
    if (!detectionResult.detected_emotions || detectionResult.detected_emotions.length === 0) {
      return null;
    }

    if (useAuthenticatedFlow) {
      const feedbackResponse = await submitFeedback({
        text,
        predicted_emotions: detectionResult.detected_emotions,
        predicted_sarcasm: detectionResult.sarcasm_detected,
        suggested_emotions: [],
        comment: '',
        language_code: language,
      });
      return feedbackResponse.data.id;
    }

    const guestIdentifier = getOrCreateGuestIdentifier();
    const feedbackResponse = await submitGuestFeedback(
      {
        text,
        predicted_emotions: detectionResult.detected_emotions,
        predicted_sarcasm: detectionResult.sarcasm_detected,
        suggested_emotions: [],
        comment: '',
        language_code: language,
      },
      guestIdentifier
    );
    return feedbackResponse.data.id;
  };

  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');

    document.title = 'EmotionWise: AI Emotion Analyzer for Messages & Conversations';
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute(
      'content',
      'Use AI to detect emotions in text messages with confidence scores and sarcasm signals. Integrate EmotionWise via API in minutes.'
    );

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null && descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    setFeedbackId(null);
    setFeedbackLoading(false);
    setVoted({});
    setVoteStatus({});
    setShowInlineFeedback(false);
    setSelectedEmotions([]);
    setFeedbackComment('');
    setFeedbackSuccess(false);
    setSarcasmFeedback(null);
    setQuotaInfo(null);

    try {
      const token = localStorage.getItem('token');
      const useAuthenticatedFlow = isAuthenticated && Boolean(token);
      const endpoint = useAuthenticatedFlow ? '/api/v1/tools/emotion-detector' : '/api/v1/tools/emotion-detector/public';

      const response = await apiClient.post(endpoint, { message: text });
      const detectionResult = response.data as EmotionResult;
      setResult(detectionResult);
      const headerQuota = readQuotaFromHeaders(response.headers);
      if (headerQuota) {
        setQuotaInfo(headerQuota);
      }
      
      // Use the language detected by the backend (using proper langdetect library)
      const language = detectionResult.detected_language || 'en';
      setDetectedLanguage(language);
      
      // Create a feedback record for voting (for both authenticated users and guests)
      setFeedbackLoading(true);
      try {
        const createdFeedbackId = await createFeedbackRecord(
          useAuthenticatedFlow,
          detectionResult,
          language
        );
        setFeedbackId(createdFeedbackId);
      } catch (feedbackError) {
        if (import.meta.env.DEV) {
          console.error('Failed to create feedback record:', feedbackError);
        }
      } finally {
        setFeedbackLoading(false);
      }
    } catch (e: any) {
      const statusCode = e.response?.status;
      const detail = e.response?.data?.detail;
      const detailMessage = typeof detail === 'string' ? detail : (detail?.detail || detail?.message);

      if (statusCode === 401) {
        setError('Your session has expired. Please login again. If this continues, contact support@emotionwise.ai and include your login time.');
      } else if (statusCode === 403) {
        if (detailMessage?.includes('X-API-Key')) {
          setError('This action requires an API key. Go to API Keys and call this endpoint using the X-API-Key header.');
        } else {
          setError((detailMessage || 'You are not authorized to perform this action.') + ' If this seems incorrect, contact support@emotionwise.ai with your account email.');
        }
      } else if (statusCode === 429) {
        const token = localStorage.getItem('token');
        const isPublic = !(isAuthenticated && Boolean(token));

        if (detail && typeof detail === 'object' && detail.limit !== undefined) {
          const used = Number(detail.used ?? 0);
          const limit = Number(detail.limit ?? 0);
          const remaining = Number(detail.remaining ?? Math.max(limit - used, 0));
          const plan = detail.plan ?? 'current';
          const resetAt = normalizeResetDate(detail.reset_at) || 'the next reset window';

          setQuotaInfo({ plan, used, limit, remaining, resetAt });
          setError(
            `Quota exceeded (${used}/${limit}) for ${plan} plan. Resets at ${resetAt}. Upgrade your plan to continue.`
          );
        } else {
          const headerQuota = readQuotaFromHeaders(e.response?.headers);
          if (headerQuota) {
            setQuotaInfo(headerQuota);
          }

          if (isPublic) {
            setError('You have reached the free trial limit (10 requests per hour). Please register for higher limits.');
          } else {
            setError('Quota exceeded for current billing period. Please retry after reset or upgrade your plan.');
          }
        }
      } else {
        setError(formatAppError(e, 'emotion-detect', detailMessage || 'An error occurred while detecting emotions.').message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setText('');
    setResult(null);
    setError('');
    setFeedbackId(null);
    setFeedbackLoading(false);
    setVoted({});
    setVoteStatus({});
    setDetectedLanguage(null);
    setShowInlineFeedback(false);
    setSelectedEmotions([]);
    setFeedbackComment('');
    setFeedbackSuccess(false);
    setSarcasmFeedback(null);
    setQuotaInfo(null);
  };

  const handleVote = async (emotion: string, vote: boolean) => {
    if (!result || !feedbackId) {
      return;
    }
    
    setVoteStatus((prev) => ({ ...prev, [emotion]: 'loading' }));
    try {
      const token = localStorage.getItem('token');
      const useAuthenticatedFlow = isAuthenticated && Boolean(token);

      if (useAuthenticatedFlow) {
        // Authenticated user voting
        await submitEmotionVote({
          feedback_id: feedbackId,
          label: emotion,
          score: result.confidence_scores[emotion] / 100,
          vote,
          comment: '',
        }, token);
      } else {
        // Guest user voting
        const guestIdentifier = getOrCreateGuestIdentifier();
        await submitGuestEmotionVote({
          feedback_id: feedbackId,
          label: emotion,
          score: result.confidence_scores[emotion] / 100,
          vote,
          comment: '',
        }, guestIdentifier);
      }
      setVoted((prev) => ({ ...prev, [emotion]: vote }));
      setVoteStatus((prev) => ({ ...prev, [emotion]: 'success' }));
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already voted')) {
        setVoteStatus((prev) => ({ ...prev, [emotion]: 'already_voted' }));
      } else {
        setVoteStatus((prev) => ({ ...prev, [emotion]: 'error' }));
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 space-y-5">
          <div className="text-center space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2">
              Emotion Detection API
            </h1>
            <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
              Analyze text for emotions and sarcasm signals. The same API you test here is production-ready.
            </p>
            <p className="text-xs sm:text-sm text-amber-200">
              Note: Sarcasm detection is experimental and may be inaccurate in edge cases.
            </p>
          </div>


          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-700 bg-slate-900/70 p-5">
              <h2 className="text-sm font-semibold text-indigo-300 uppercase tracking-wide mb-3">How It Works</h2>
              <div className="flex flex-col gap-3">
                <div className="rounded-xl bg-indigo-500/10 border border-indigo-400/30 px-4 py-3 text-sm font-medium text-indigo-200">
                  1. Send text to <code className="font-mono">/api/v1/tools/emotion-detector</code>
                </div>
                <div className="text-center text-indigo-300">↓</div>
                <div className="rounded-xl bg-violet-500/10 border border-violet-400/30 px-4 py-3 text-sm font-medium text-violet-200">
                  2. Get top emotions + confidence + sarcasm signal
                </div>
                <div className="text-center text-violet-300">↓</div>
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 px-4 py-3 text-sm font-medium text-emerald-200">
                  3. No data stored — results returned and discarded
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-900/70 p-5 space-y-3">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">API Quick Start</h2>
              <div className="rounded-xl bg-gray-900 text-gray-100 p-3 text-xs overflow-x-auto">
                <pre>{`POST /api/v1/tools/emotion-detector
X-API-Key: <api_key>

{
  "message": "I am happy"
}`}</pre>
              </div>
              <div className="rounded-xl bg-slate-950/80 border border-slate-700 p-3 text-xs overflow-x-auto text-slate-200">
                <pre>{`{
  "detected_emotions": ["joy"],
  "confidence_scores": {"joy": 98.4},
  "sarcasm_detected": false,
  "detected_language": "en"
}`}</pre>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-md bg-purple-500/20 text-purple-300">Auth: X-API-Key</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-slate-900/80 backdrop-blur-sm shadow-2xl rounded-3xl border border-slate-700 overflow-hidden">
          <div className="p-8 sm:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Status Indicator */}
              <div className="flex justify-center sm:justify-start">
                {isAuthenticated ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Logged In - {getPlanLabel(currentPlan)}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-sky-500/15 text-sky-300 border border-sky-500/40">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Guest Sandbox - 10 requests per hour
                  </span>
                )}
              </div>

              {isAuthenticated && quotaInfo && (
                <div className="flex justify-center sm:justify-start">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-500/15 text-indigo-200 border border-indigo-500/40">
                    Usage: {quotaInfo.used ?? '—'} / {quotaInfo.limit ?? '—'} · Remaining: {quotaInfo.remaining ?? '—'}
                    {quotaInfo.resetAt ? ` · Resets: ${quotaInfo.resetAt}` : ''}
                  </span>
                </div>
              )}

              {/* Text Input */}
              <div>
                <label htmlFor="text-input" className="block text-lg font-semibold text-slate-100 mb-4">
                  Enter your text to analyze emotions
                </label>
                <div className="relative">
                  <textarea
                    id="text-input"
                    rows={6}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="w-full px-6 py-4 text-lg border-2 border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-300 text-slate-100 placeholder-slate-500 resize-none bg-slate-950/60 backdrop-blur-sm"
                    placeholder="Type or paste your message here... (e.g., 'I'm so excited about this new opportunity!')"
                    required
                    maxLength={1000}
                  />
                  <div className="absolute bottom-3 right-3 text-sm text-slate-500">
                    {text.length}/1000 characters
                  </div>
                </div>
                {text.length < 10 && text.length > 0 && (
                  <p className="mt-2 text-sm text-amber-300">
                    💡 Tip: Try entering at least 10 characters for better emotion detection
                  </p>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="animate-in slide-in-from-top-2 duration-300">
                  <div className="bg-red-500/10 border-2 border-red-500/30 rounded-2xl p-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-red-300">Error</h3>
                        <p className="mt-1 text-sm text-red-400">{error}</p>
                        <p className="mt-2 text-xs text-red-500">
                          If this error persists, please contact us at{' '}
                          <a 
                            href="mailto:support@emotionwise.ai" 
                            className="text-red-700 hover:text-red-900 underline font-medium"
                          >
                            support@emotionwise.ai
                          </a>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={loading || text.trim().length === 0}
                  className="flex-1 flex items-center justify-center py-4 px-8 border border-transparent rounded-2xl shadow-lg text-lg font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 active:scale-95"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      Analyzing emotions...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Detect Emotions
                    </>
                  )}
                </button>
                
                {(text || result) && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="px-6 py-4 border-2 border-slate-600 rounded-2xl text-lg font-medium text-slate-300 hover:bg-slate-800 hover:border-slate-500 focus:outline-none focus:ring-4 focus:ring-slate-500/20 transition-all duration-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Results Section */}
        {result && (
          <div className="mt-8 animate-in slide-in-from-bottom-2 duration-500">
            <div className="bg-slate-800/60 rounded-2xl p-6 border border-slate-700">
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Analysis Results</h2>

              {/* Emotions Table */}
              <div className="mb-6 overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-700">
                    <thead className="bg-slate-800">
                      <tr>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Emotion
                        </th>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Confidence
                        </th>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Feedback
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-slate-900 divide-y divide-slate-700">
                      {Array.isArray(result.detected_emotions) && result.detected_emotions.length > 0 ? (
                        result.detected_emotions.map((emotion: string) => (
                          <tr key={emotion} className="hover:bg-slate-800 transition-colors duration-150">
                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-700 text-lg shadow-sm mr-3">
                                  {EMOTICONS[emotion as EmotionType] || '❓'}
                                </span>
                                <span className="text-sm font-medium text-slate-200 capitalize">
                                  {emotion}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg font-bold text-slate-100 min-w-[3rem]">
                                  {result.confidence_scores[emotion]}%
                                </span>
                                <div className="flex-1 max-w-32">
                                  <div className="w-full bg-slate-700 rounded-full h-3">
                                    <div
                                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out"
                                      style={{ width: `${result.confidence_scores[emotion]}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                              {/* Voting Buttons */}
                              <div className="flex items-center space-x-2">
                                {feedbackLoading ? (
                                  <span className="text-xs text-slate-500 px-3 py-1 bg-slate-800 rounded-full">
                                    Loading...
                                  </span>
                                ) : !feedbackId ? (
                                  <span className="text-xs text-slate-600">—</span>
                                ) : voted[emotion] === undefined ? (
                                  <>
                                    <button
                                      className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-all duration-200 disabled:opacity-50 text-lg"
                                      onClick={() => handleVote(emotion, true)}
                                      aria-label="Accurate emotion detection"
                                      disabled={voteStatus[emotion] === 'loading'}
                                      title="This emotion is accurate"
                                    >
                                      {voteStatus[emotion] === 'loading' ? '⏳' : '👍'}
                                    </button>
                                    <button
                                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200 disabled:opacity-50 text-lg"
                                      onClick={() => handleVote(emotion, false)}
                                      aria-label="Inaccurate emotion detection"
                                      disabled={voteStatus[emotion] === 'loading'}
                                      title="This emotion is not accurate"
                                    >
                                      {voteStatus[emotion] === 'loading' ? '⏳' : '👎'}
                                    </button>
                                  </>
                                ) : voteStatus[emotion] === 'success' ? (
                                  <span className="text-sm text-emerald-400 font-medium px-3 py-1 bg-emerald-500/10 rounded-full">
                                    ✓ Thanks!
                                  </span>
                                ) : voteStatus[emotion] === 'already_voted' ? (
                                  <span className="text-sm text-indigo-300 font-medium px-3 py-1 bg-indigo-500/10 rounded-full">
                                    Already voted
                                  </span>
                                ) : voteStatus[emotion] === 'error' ? (
                                  <span className="text-sm text-red-400 font-medium px-3 py-1 bg-red-500/10 rounded-full">
                                    Error
                                  </span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-4 sm:px-6 py-8 text-sm text-gray-500 text-center" colSpan={3}>
                            <div className="flex flex-col items-center">
                              <svg className="h-12 w-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <p>No emotions detected in this text</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Secondary Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sarcasm Detection */}
                <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-600 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-100">Sarcasm Detection:</span>
                    <div className="flex flex-col items-end gap-1">
                      {result.sarcasm_detected ? (
                        <span className="flex items-center space-x-2 text-orange-300 bg-orange-500/20 rounded-full px-3 py-1 font-semibold text-sm">
                          <span>⚡</span>
                          <span>Detected</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-2 text-slate-300 bg-slate-700 rounded-full px-3 py-1 font-semibold text-sm">
                          <span>✨</span>
                          <span>Not detected</span>
                        </span>
                      )}
                      {result.sarcasm_score != null && (
                        <span className="text-xs text-slate-400">
                          Confidence: {result.sarcasm_score}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                    <p className="text-xs text-amber-200">
                      Sarcasm detection is in active development. Treat this signal as experimental and verify with context.
                    </p>
                  </div>
                </div>

                {/* Language Detection */}
                {detectedLanguage && (
                  <div className="flex items-center justify-between p-4 bg-slate-800/80 rounded-xl border border-slate-600 shadow-sm">
                    <span className="font-medium text-slate-100">Language:</span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-500/20 text-indigo-200">
                      {detectedLanguage.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Recommendation */}
              {result.recommendation && (
                <div className="mt-6 p-6 bg-indigo-500/10 rounded-xl border border-indigo-500/30 shadow-sm">
                  <div className="flex items-start">
                    <svg className="h-6 w-6 text-indigo-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="font-semibold text-indigo-200 mb-1">💡 Recommendation</h3>
                      <p className="text-indigo-100 leading-relaxed">{result.recommendation}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Inline Feedback Section */}
              {result.detected_emotions && result.detected_emotions.length > 0 && !showInlineFeedback && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => {
                      setShowInlineFeedback(true);
                    }}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                      feedbackSuccess
                        ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-400/50 focus:ring-emerald-500'
                        : 'text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20 hover:border-indigo-400/50 focus:ring-indigo-500'
                    }`}
                  >
                    {feedbackSuccess ? (
                      <>
                        <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Feedback Submitted - Edit More
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Suggest Additional Emotions
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Inline Feedback Form */}
              {showInlineFeedback && result.detected_emotions && detectedLanguage && (
                <InlineFeedbackForm
                  text={text}
                  detectedEmotions={result.detected_emotions}
                  predictedEmotions={result.detected_emotions}
                  predictedSarcasm={result.sarcasm_detected}
                  languageCode={detectedLanguage}
                  selectedEmotions={selectedEmotions}
                  comment={feedbackComment}
                  onEmotionsChange={setSelectedEmotions}
                  onCommentChange={setFeedbackComment}
                  sarcasmFeedback={sarcasmFeedback}
                  onSarcasmFeedbackChange={setSarcasmFeedback}
                  onSuccess={() => {
                    setFeedbackSuccess(true);
                    setShowInlineFeedback(false);
                  }}
                  onCancel={() => setShowInlineFeedback(false)}
                />
              )}
            </div>
          </div>
        )}

        {/* FAQ Section for SEO */}
        <div className="mt-12 bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-6 sm:p-8">
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-200">What is an AI emotion detector?</h3>
                  <p className="text-slate-400">
                    It's a tool that uses natural language processing (NLP) and machine learning to analyze the emotional tone of written text, identifying emotions like anger, joy, sadness, fear, or surprise.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-200">Is my text data private?</h3>
                  <p className="text-slate-400">
                    Yes. EmotionWise does not store any text you submit or any analysis results. Your messages are processed in real time and discarded immediately after the response is returned. We never sell or share your data with third parties.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-200">Who is EmotionWise built for?</h3>
                  <p className="text-slate-400">
                    Developers and product teams building applications that need emotion or sentiment signals — chatbots, content moderation, CX analytics, user research tools, and more.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
